import React from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Tooltip,
    useTheme,
    Card,
    CardContent
} from '@mui/material';
import { tokens } from '../../theme';

const CompetencyMatrix = ({ data }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    if (!data) {
        return (
            <Box p={2}>
                <Typography variant="body2" sx={{ color: colors.grey[600] }}>
                    No competency matrix data available.
                </Typography>
            </Box>
        );
    }

    const { skills, employees, cells, meta } = data;

    // Create a map for quick cell lookup
    const cellMap = {};
    cells.forEach(cell => {
        cellMap[`${cell.employeeId}-${cell.skillId}`] = cell;
    });

    // Get status color with proper theme consistency
    const getStatusColor = (status) => {
        switch (status) {
            case 'met': return colors.green[400];
            case 'partial': return colors.Yellow[300];
            case 'critical': return colors.redAccent[500];
            default: return colors.grey[500];
        }
    };

    // Get status background color (lighter version for better readability)
    const getStatusBgColor = (status) => {
        switch (status) {
            case 'met': return colors.green[100];
            case 'partial': return colors.Yellow[100];
            case 'critical': return colors.redAccent[100];
            default: return colors.grey[100];
        }
    };

    // Get status text
    const getStatusText = (status) => {
        switch (status) {
            case 'met': return 'Met';
            case 'partial': return 'Partial';
            case 'critical': return 'Critical';
            default: return 'Unknown';
        }
    };

    return (
        <Box>

            <TableContainer
                component={Paper}
                sx={{
                    maxHeight: 600,
                    overflow: 'auto',
                    // backgroundColor: theme.palette.mode === 'dark' ? colors.primary[800] : colors.grey[50],
                    borderRadius: 2,
                    border: `1px solid ${colors.grey[300]}`
                }}
            >
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell
                                sx={{
                                    background: theme.palette.mode === 'dark'
                                        ? `linear-gradient(135deg, ${colors.primary[850]}, ${colors.primary[200]})`
                                        : `linear-gradient(135deg, ${colors.primary[400]}, ${colors.primary[500]})`,
                                    color: colors.grey[50],
                                    fontWeight: 700,
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 3,
                                    borderRight: `2px solid ${colors.grey[300]}`,
                                    minWidth: 160
                                }}
                            >
                                Employee / Skill
                            </TableCell>
                            {skills.map(skill => (
                                <TableCell
                                    key={skill.id}
                                    sx={{
                                        background: theme.palette.mode === 'dark'
                                            ? `linear-gradient(135deg, ${colors.primary[600]}, ${colors.primary[700]})`
                                            : `linear-gradient(135deg, ${colors.primary[400]}, ${colors.primary[500]})`,
                                        color: colors.grey[50],
                                        fontWeight: 600,
                                        minWidth: 140,
                                        textAlign: 'center'
                                    }}
                                >
                                    <Tooltip
                                        title={`ID: ${skill.id} | Required Level: ${skill.required_min || 'N/A'} - ${skill.required_max || 'N/A'}`}
                                        arrow
                                    >
                                        <Box sx={{ cursor: 'help' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                {skill.name}
                                            </Typography>
                                            <Chip
                                                label={skill.required_min !== null ? `${skill.required_min}-${skill.required_max}` : 'N/A'}
                                                size="small"
                                                sx={{
                                                    backgroundColor: colors.grey[200],
                                                    color: colors.grey[700],
                                                    fontSize: '0.65rem',
                                                    height: 18,
                                                    fontWeight: 600
                                                }}
                                            />
                                        </Box>
                                    </Tooltip>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {employees.map((employee, employeeIndex) => (
                            <TableRow
                                key={employee.id}
                                // sx={{
                                //     '&:nth-of-type(odd)': {
                                //         backgroundColor: theme.palette.mode === 'dark'
                                //             ? colors.primary[900]
                                //             : colors.grey[25]
                                //     },
                                //     '&:hover': {
                                //         backgroundColor: theme.palette.mode === 'dark'
                                //             ? colors.primary[700]
                                //             : colors.grey[200]
                                //     }
                                // }}
                            >
                                <TableCell
                                    component="th"
                                    scope="row"
                                    sx={{
                                        // backgroundColor: theme.palette.background.secondCard,
                                        // color: theme.palette.mode === 'dark' ? colors.grey[100] : colors.grey[800],
                                        fontWeight: 600,
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 2,
                                        borderRight: `2px solid ${colors.grey[300]}`
                                    }}
                                >
                                    <Box>
                                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                            {employee.name}
                                        </Typography>
                                        <Chip
                                            label={`Email: ${employee.email}`}
                                            size="small"
                                            sx={{
                                                backgroundColor: colors.grey[theme.palette.mode === 'dark' ? 600 : 300],
                                                color: colors.grey[theme.palette.mode === 'dark' ? 200 : 700],
                                                fontSize: '1rem',
                                                height: 18
                                            }}
                                        />
                                    </Box>
                                </TableCell>
                                {skills.map(skill => {
                                    const cell = cellMap[`${employee.id}-${skill.id}`];
                                    return (
                                        <TableCell
                                            key={skill.id}
                                            align="center"
                                            sx={{
                                                backgroundColor: cell
                                                    ? getStatusBgColor(cell.status)
                                                    : colors.grey[200],
                                                border: `1px solid ${colors.grey[300]}`,
                                                position: 'relative',
                                                '&:hover': {
                                                    boxShadow: `inset 0 0 0 2px ${colors.primary[400]}`,
                                                    zIndex: 1
                                                }
                                            }}
                                        >
                                            <Tooltip
                                                title={
                                                    cell ?
                                                        `Current Level: ${cell.current} | Required: ${cell.required || 'N/A'} | Status: ${getStatusText(cell.status)}` :
                                                        'No competency data available for this skill'
                                                }
                                                arrow
                                                placement="top"
                                            >
                                                <Box sx={{ cursor: 'help', py: 1 }}>
                                                    {cell ? (
                                                        <>
                                                            <Typography
                                                                variant="h6"
                                                                sx={{
                                                                    color: colors.grey[theme.palette.mode === 'dark' ? 800 : 700],
                                                                    fontWeight: 700,
                                                                    mb: 0.5
                                                                }}
                                                            >
                                                                {cell.current}
                                                            </Typography>
                                                            <Chip
                                                                label={getStatusText(cell.status)}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: getStatusColor(cell.status),
                                                                    color: colors.grey[50],
                                                                    fontSize: '0.7rem',
                                                                    height: 22,
                                                                    fontWeight: 600,
                                                                    boxShadow: 1
                                                                }}
                                                            />
                                                        </>
                                                    ) : (
                                                        <Typography
                                                            variant="h6"
                                                            sx={{
                                                                color: colors.grey[500],
                                                                fontWeight: 400
                                                            }}
                                                        >
                                                            —
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Tooltip>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Enhanced Legend */}
            <Card sx={{ mt: 3 }}>
                <CardContent sx={{
                    backgroundColor: theme.palette.background.secondCard,
                    border: `1px solid ${colors.grey[300]}`
                }}>
                    <Typography variant="h6" gutterBottom sx={{
                        color: colors.grey[theme.palette.mode === 'dark' ? 700 : 600],
                        fontWeight: 600,
                        mb: 2
                    }}>
                        Status Legend
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{
                                width: 24,
                                height: 24,
                                backgroundColor: getStatusColor('met'),
                                borderRadius: 1,
                                boxShadow: 1
                            }} />
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: colors.grey[theme.palette.mode === 'dark' ? 700 : 600] }}>
                                    Met
                                </Typography>
                                <Typography variant="caption" sx={{ color: colors.grey[500] }}>
                                    Competency meets requirements
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{
                                width: 24,
                                height: 24,
                                backgroundColor: getStatusColor('partial'),
                                borderRadius: 1,
                                boxShadow: 1
                            }} />
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: colors.grey[theme.palette.mode === 'dark' ? 700 : 600] }}>
                                    Partial
                                </Typography>
                                <Typography variant="caption" sx={{ color: colors.grey[500] }}>
                                    Partial competency gap
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{
                                width: 24,
                                height: 24,
                                backgroundColor: getStatusColor('critical'),
                                borderRadius: 1,
                                boxShadow: 1
                            }} />
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: colors.grey[theme.palette.mode === 'dark' ? 700 : 600] }}>
                                    Critical
                                </Typography>
                                <Typography variant="caption" sx={{ color: colors.grey[500] }}>
                                    Significant competency gap
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default CompetencyMatrix;